import { useState, useEffect, useCallback } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { ChevronLeft, Search, RefreshCw, X, Loader2, Undo2, Trophy, Coins } from "lucide-react";
import { useFb } from "../../utils/football";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";

const SORTS = [
  ["total_points", "Points"],
  ["lifetime_spend_aed", "Spend"],
  ["match_days_played", "Match Days"],
  ["referral_count", "Referrals"],
  ["super_subs_owned", "Super Subs"],
  ["shields_owned", "Shields"],
  ["display_name", "Name"],
];

const UserDetailModal = ({ userId, onClose }) => {
  const fb = useFb();
  const [data, setData] = useState(null);
  const [predictor, setPredictor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refunding, setRefunding] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cons, pred] = await Promise.all([
        fb.get(`/football/admin/users/${userId}/consumables`),
        fb.get(`/football/admin/users/${userId}/predictor-history`).catch(() => null),
      ]);
      setData(cons);
      setPredictor(pred);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load user detail");
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const refund = async (purchaseId, label) => {
    if (!window.confirm(`Refund "${label}" back to this user's inventory? (No money is refunded.)`)) return;
    setRefunding(purchaseId);
    try {
      await fb.post(`/football/admin/users/${userId}/refund-consumable`, { purchase_id: purchaseId });
      toast.success("Consumable refunded to inventory");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    }
    setRefunding("");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center" onClick={onClose} data-testid="fb-user-detail-modal">
      <div className="football-theme max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-border bg-card p-4 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-extrabold text-foreground">{data?.display_name || "User"} · Consumables</h2>
          <button onClick={onClose} className="rounded-lg bg-secondary p-1.5" data-testid="fb-user-detail-close"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="flex h-32 items-center justify-center"><Loader2 className="animate-spin text-muted-foreground" /></div>
        ) : data ? (
          <div className="space-y-4">
            {/* Inventory summary */}
            <div className="grid grid-cols-3 gap-2" data-testid="fb-user-inventory">
              {[["super_sub", "Super Subs"], ["no_penalty_shield", "Shields"], ["captain_boost", "Cap Boosts"]].map(([k, lbl]) => (
                <div key={k} className="rounded-xl border border-border bg-background p-2.5 text-center">
                  <div className="text-lg font-extrabold wc-gold">{data.inventory?.[k] ?? 0}</div>
                  <div className="text-[11px] text-muted-foreground">{lbl}</div>
                </div>
              ))}
            </div>

            {/* Purchase history */}
            <div>
              <h3 className="mb-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">Purchase History</h3>
              {data.purchase_history?.length ? (
                <div className="space-y-1.5" data-testid="fb-user-purchases">
                  {data.purchase_history.map((p) => (
                    <div key={p.purchase_id} className="flex items-center justify-between gap-2 rounded-lg border border-border bg-background p-2.5">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-bold">{p.label} {p.is_pack && <span className="text-[10px] text-amber-400">×{p.quantity}</span>}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {(p.purchased_at || "").slice(0, 10)} · {p.platform || "—"} · AED {p.price_aed} · {p.remaining_uses}/{p.quantity} left
                        </div>
                      </div>
                      <button
                        onClick={() => refund(p.purchase_id, p.label)}
                        disabled={refunding === p.purchase_id || p.remaining_uses >= p.quantity}
                        className="flex shrink-0 items-center gap-1 rounded-lg border border-amber-400/40 bg-amber-400/10 px-2.5 py-1.5 text-[11px] font-bold text-amber-300 disabled:opacity-40"
                        data-testid={`fb-refund-${p.purchase_id}`}
                      >
                        {refunding === p.purchase_id ? <Loader2 size={12} className="animate-spin" /> : <Undo2 size={12} />} Refund
                      </button>
                    </div>
                  ))}
                </div>
              ) : <p className="text-xs text-muted-foreground">No purchases.</p>}
            </div>

            {/* Usage history */}
            <div>
              <h3 className="mb-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">Usage History</h3>
              {data.usage_history?.length ? (
                <div className="space-y-1.5" data-testid="fb-user-usage">
                  {data.usage_history.map((u, i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg border border-border bg-background p-2.5 text-sm">
                      <span className="font-bold">{u.label} {u.count > 1 && <span className="text-[11px] text-muted-foreground">×{u.count}</span>}</span>
                      <span className="text-[11px] text-muted-foreground">{u.match_day} · {u.outcome_points} pts</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-xs text-muted-foreground">No usage yet.</p>}
            </div>

            {/* Predictor activity */}
            <div data-testid="fb-user-predictor">
              <h3 className="mb-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">Predictor Activity</h3>
              {predictor ? (
                <>
                  <div className="grid grid-cols-4 gap-2">
                    {[["seen", "Seen"], ["answered", "Answered"], ["correct", "Correct"], ["points", "Points"]].map(([k, lbl]) => (
                      <div key={k} className="rounded-xl border border-border bg-background p-2 text-center">
                        <div className="text-base font-extrabold wc-gold">{predictor.stats?.[k] ?? 0}</div>
                        <div className="text-[10px] text-muted-foreground">{lbl}</div>
                      </div>
                    ))}
                  </div>
                  {predictor.recent?.length ? (
                    <div className="mt-2 space-y-1.5">
                      {predictor.recent.map((r, i) => (
                        <div key={i} className="rounded-lg border border-border bg-background p-2.5">
                          <div className="truncate text-sm font-bold">{r.question_text || "—"}</div>
                          <div className="text-[11px] text-muted-foreground">
                            {r.match_day} · Ans: <b className="text-foreground">{r.user_answer ?? "—"}</b>
                            {r.resolved && <> · Correct: <b className="text-foreground">{r.correct_answer ?? "—"}</b> · {r.points_earned || 0} pts</>}
                            {!r.resolved && <> · pending</>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : <p className="mt-2 text-xs text-muted-foreground">No submissions.</p>}
                </>
              ) : <p className="text-xs text-muted-foreground">No predictor data.</p>}
            </div>
          </div>
        ) : <p className="text-sm text-muted-foreground">No data.</p>}
      </div>
    </div>
  );
};

export default function AdminFootballUsersPage() {
  const fb = useFb();
  const navigate = useNavigate();
  const { dbUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("total_points");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fb.get("/football/admin/users", { search: search || undefined, sort_by: sortBy, order: "desc", limit: 100, skip: 0 });
      setUsers(d.users || []);
      setTotal(d.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy]);

  useEffect(() => { load(); }, [load]);

  if (dbUser && !dbUser.is_admin) return <Navigate to="/" replace />;

  return (
    <div className="football-theme min-h-screen bg-background pb-12" data-testid="admin-football-users-page">
      <div className="wc-hero-gradient px-4 pb-5 pt-4">
        <button onClick={() => navigate("/admin")} className="mb-2 inline-flex items-center gap-1 text-sm text-white/80" data-testid="admin-fb-users-back"><ChevronLeft size={18} /> Admin</button>
        <h1 className="text-xl font-extrabold text-white">Football — Users</h1>
        <p className="mt-1 text-sm text-white/70">{total} football managers · stats, referrals & consumables.</p>
      </div>

      <div className="mx-auto max-w-3xl px-4">
        <div className="mt-4 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, team, code, country…"
              className="w-full rounded-xl border border-border bg-card py-2.5 pl-9 pr-3 text-sm"
              data-testid="fb-users-search"
            />
          </div>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="rounded-xl border border-border bg-card px-3 py-2.5 text-sm font-bold" data-testid="fb-users-sort">
            {SORTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <button onClick={load} className="rounded-xl bg-secondary p-2.5" data-testid="fb-users-refresh"><RefreshCw size={18} /></button>
        </div>

        {loading ? (
          <div className="mt-5 h-40 animate-pulse rounded-xl bg-secondary/50" />
        ) : users.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground" data-testid="fb-users-empty">No football users found.</div>
        ) : (
          <div className="mt-4 space-y-2" data-testid="fb-users-list">
            {users.map((u) => (
              <button
                key={u.user_id}
                onClick={() => setSelected(u.user_id)}
                className="w-full rounded-xl border border-border bg-card p-3 text-left transition-colors hover:border-amber-400/40"
                data-testid={`fb-user-row-${u.user_id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-extrabold text-foreground">{u.display_name}</div>
                    <div className="truncate text-[11px] text-muted-foreground">
                      {u.football_team_name || "—"} · {u.country || "—"}{u.gender ? ` · ${u.gender}` : ""}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="flex items-center gap-1 text-sm font-extrabold wc-gold"><Trophy size={12} /> {u.total_points}</div>
                    <div className="text-[11px] text-muted-foreground">{u.match_days_played} match days</div>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                  <span>Code: <span className="font-mono text-foreground">{u.own_referral_code || "—"}</span></span>
                  {u.used_referral_code && <span>Used: <span className="font-mono">{u.used_referral_code}</span></span>}
                  <span>Refs: <span className="font-bold text-foreground">{u.referral_count}</span></span>
                  <span>SuperSubs: <span className="font-bold text-foreground">{u.super_subs_owned}</span></span>
                  <span>Shields: <span className="font-bold text-foreground">{u.shields_owned}</span></span>
                  <span className="flex items-center gap-0.5"><Coins size={11} /> AED {u.lifetime_spend_aed}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected && <UserDetailModal userId={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
